import { useEffect, useState } from "react";
import { toast } from "sonner";
import { resendEmailChange, cancelEmailChange, formatApiErrorDetail } from "../../lib/api";
import { Btn } from "./ui";

const errMsg = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

/* Pending sign-in-email change: shows delivery state and offers resend / cancel. */
export default function EmailChangePending({ pending, onChange }) {
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(pending.canResendInSeconds || 0);

  useEffect(() => {
    setWait(pending.canResendInSeconds || 0);
  }, [pending.canResendInSeconds, pending.lastSentAt]);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setInterval(() => setWait((w) => (w > 0 ? w - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [wait]);

  const resend = async () => {
    setBusy(true);
    try {
      onChange(await resendEmailChange());
      toast.success(`Confirmation re-sent to ${pending.newEmail}.`, { duration: 7000 });
    } catch (e) { toast.error(errMsg(e), { duration: 9000 }); }
    setBusy(false);
  };

  const cancel = async () => {
    setBusy(true);
    try {
      onChange(await cancelEmailChange());
      toast.success("Pending email change cancelled. Your sign-in email is unchanged.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const expires = new Date(pending.expiresAt);
  return (
    <div className="mt-6 border border-gold/30 bg-gold/[0.04] p-5" data-testid="email-change-pending">
      <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-3">PENDING EMAIL CHANGE</p>
      <p className="font-mono text-[9px] tracking-[0.15em] text-white/50 leading-relaxed">
        AWAITING CONFIRMATION AT{" "}
        <span className="text-white/85" data-testid="pending-new-email">{pending.newEmail.toUpperCase()}</span>
        {" "}— YOUR CURRENT SIGN-IN EMAIL STAYS ACTIVE UNTIL THE LINK IS CONFIRMED. THE LINK IS SINGLE-USE AND
        EXPIRES AT {expires.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.
      </p>
      <p className="font-mono text-[9px] tracking-[0.15em] text-white/35 mt-3" data-testid="pending-delivery-state">
        {pending.lastDelivery?.accepted
          ? `PROVIDER ACCEPTED THE MESSAGE (HTTP ${pending.lastDelivery.providerStatus}) — ACCEPTANCE IS NOT PROOF OF INBOX DELIVERY. CHECK SPAM.`
          : "NO SUCCESSFUL DISPATCH RECORDED FOR THIS REQUEST."}
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Btn onClick={resend} disabled={busy || wait > 0 || pending.resendsLeft === 0}
             testid="email-change-resend">
          {wait > 0 ? `RESEND IN ${wait}S` : "RESEND VERIFICATION EMAIL"}
        </Btn>
        <button onClick={cancel} disabled={busy} data-testid="email-change-cancel"
                className="font-mono text-[10px] tracking-[0.25em] text-white/45 hover:text-red-400 transition-colors disabled:opacity-40">
          CANCEL EMAIL CHANGE
        </button>
        <span className="font-mono text-[9px] tracking-[0.15em] text-white/25" data-testid="email-change-resends-left">
          {pending.resendsLeft} RESEND{pending.resendsLeft === 1 ? "" : "S"} LEFT
        </span>
      </div>
    </div>
  );
}
